// csa_flow/core — pure logic, no file I/O (browser-compatible)
// used by both CLI (mod.ts) and web (ent/web/mod/ftr-f19)

import type {BasicDocInput, BasicDocLine, BasicDocTotalPart, CcyCode, KVPair} from '../types'
import type {ParsedSheet} from '@ts/mod/excel_gen/field_sheet_parse'
import {money as money_make} from '../money_fmt'

// --

export type PendingDoc = {
	short: string
	seq_typ: string
	idx: number
	doc_row: Record<string, unknown>
	lines: Record<string, unknown>[]
	contact_row?: Record<string, unknown>
}

export type WorkbookStats = {
	sheet_count: number
	sheets: {name: string; row_count: number; col_count: number}[]
	contact_count: number
	line_count: number
	doc_count: number
	exported_count: number
	pending_count: number
}

export type CsaFlowCoreResult = {
	pending_docs: PendingDoc[]
	exported_shorts: string[]
	stats: WorkbookStats
}

// --

/** analyze parsed sheets: resolve computed columns, find pending docs */
export const csa_flow_analyze = (sheets: ParsedSheet[]): CsaFlowCoreResult => {
	const by_name = new Map(sheets.map(s => [s.name, s]))

	const kontakter = by_name.get('Kontakter')
	const rader = by_name.get('Rader')
	const dokument = by_name.get('Dokument')
	const exporter = by_name.get('Exporter')
	const dok_serier = by_name.get('Dok.serier')

	if (!dokument) throw new Error('missing sheet: Dokument')
	if (!rader) throw new Error('missing sheet: Rader')

	// build doc_seq lookup: short → {typ, year}
	const doc_seq_map = new Map<string, {typ: string; year: number}>()
	if (dok_serier) {
		for (const row of dok_serier.rows) {
			const typ = str(row['doc_seq.typ'])
			const year = num(row['doc_seq.year'])
			if (typ && year) {
				const short = str(row['doc_seq.short']) || `${typ}${String(year).slice(-2)}`
				doc_seq_map.set(short, {typ, year})
			}
		}
	}

	// resolve computed columns in Dokument rows
	for (const doc_row of dokument.rows) {
		const seq = str(doc_row['doc.seq'])
		const idx = num(doc_row['doc.idx'])
		if (!str(doc_row['doc.short']) && seq && idx) {
			doc_row['doc.short'] = `${seq}-${String(idx).padStart(3, '0')}`
		}
		if (!str(doc_row['doc.seq_typ']) && seq) {
			const ds = doc_seq_map.get(seq)
			if (ds) {
				doc_row['doc.seq_typ'] = ds.typ
				if (!num(doc_row['doc.seq_year'])) doc_row['doc.seq_year'] = ds.year
			}
		}
	}

	// find already-exported doc shorts
	const exported_shorts = new Set<string>()
	if (exporter) {
		for (const row of exporter.rows) {
			const short = str(row['export_batch_item.doc'])
			if (short) exported_shorts.add(short)
		}
	}

	// determine pending docs
	const pending: PendingDoc[] = []
	for (const doc_row of dokument.rows) {
		const short = str(doc_row['doc.short'])
		const seq_typ = str(doc_row['doc.seq_typ'])
		const idx = num(doc_row['doc.idx'])
		if (!short || !seq_typ) continue
		if (!seq_typ.startsWith('S')) continue
		if (seq_typ.startsWith('SI')) continue
		if (exported_shorts.has(short)) continue

		const lines = rader.rows.filter(r => str(r['author_payout_line.doc']) === short)

		const author_name = str(doc_row['payout_doc.author'])
		const contact_row = author_name && kontakter
			? kontakter.rows.find(r => str(r['party.name_norm']) === author_name)
			: undefined

		pending.push({short, seq_typ, idx, doc_row, lines, contact_row})
	}

	// stats
	const stats: WorkbookStats = {
		sheet_count: sheets.length,
		sheets: sheets.map(s => ({name: s.name, row_count: s.rows.length, col_count: s.col_keys.length})),
		contact_count: kontakter?.rows.length ?? 0,
		line_count: rader.rows.length,
		doc_count: dokument.rows.length,
		exported_count: exported_shorts.size,
		pending_count: pending.length,
	}

	return {pending_docs: pending, exported_shorts: [...exported_shorts], stats}
}

// --

/** build BasicDocInput from a PendingDoc (pure, no I/O) */
export const build_basic_doc_input = (doc: PendingDoc, dok_typer?: ParsedSheet, kontakter?: ParsedSheet): BasicDocInput => {
	const row = doc.doc_row
	const ccy = (str(row['payout_doc.ccy']) || 'SEK') as CcyCode
	const period = str(row['payout_doc.period']) || ''
	const issued = str(row['doc.issued']) || ''

	// resolve doc_seq_typ template data
	const typ_row = dok_typer?.rows.find(r => str(r['doc_seq_typ.short']) === doc.seq_typ)
	const pdf_title = str(typ_row?.['doc_seq_typ_pdf.title']) || doc.seq_typ
	const pdf_subtitle = str(typ_row?.['doc_seq_typ_pdf.subtitle']) || ''
	const table_text = str(typ_row?.['doc_seq_typ_pdf.table_text']) || ''
	const footer_text = str(typ_row?.['doc_seq_typ_pdf.footer_text']) || ''

	// resolve sender from dok_typer → kontakter lookup
	const sender_ref = str(typ_row?.['doc_seq_typ_pdf.sender']) || ''
	const {src, logo_ref} = build_src_from_sender(sender_ref, kontakter)
	const dst = build_dst_from_contact(doc.contact_row)

	const lines: BasicDocLine[] = doc.lines.map(l => {
		const gross_raw = num(l['author_payout_line.gross_amount'])
		const pct = num(l['author_payout_line.commission_pct'])
		const commission_raw = num(l['author_payout_line.commission_amount'] ?? (gross_raw * pct))
		const net_raw = gross_raw - commission_raw

		return {
			work_title: str(l['doc_line.title']) || '',
			desc: str(l['doc_line.subtitle']) || undefined,
			who_title: str(l['work_statement_line.theater']) || undefined,
			amount_gross: money_make(Math.round(gross_raw * 100), ccy),
			amount_commission: money_make(Math.round(commission_raw * 100), ccy),
			amount_net: money_make(Math.round(net_raw * 100), ccy),
		}
	})

	const total_gross = lines.reduce((s, l) => s + l.amount_gross.amount, 0)
	const total_commission = lines.reduce((s, l) => s + (l.amount_commission?.amount ?? 0), 0)
	const total_net = lines.reduce((s, l) => s + l.amount_net.amount, 0)

	const totals_parts: BasicDocTotalPart[] = [
		{title: 'Brutto totalt', amount: {amount: total_gross, ccy}},
		{title: 'Provision totalt', amount: {amount: total_commission, ccy}},
	]

	const doc_kv: KVPair[] = []
	if (issued) doc_kv.push({key: 'Datum', value: issued})
	if (period) doc_kv.push({key: 'Period', value: period})
	doc_kv.push({key: 'Dok.nr', value: doc.short})

	// per-doc kv from doc row
	parse_kv_str(str(row['doc.kv_doc']), doc_kv)
	// type-level kv from dok_typer
	parse_kv_str(str(typ_row?.['doc_seq_typ_pdf.doc_kv']), doc_kv)

	const doc_kv_small: KVPair[] = []
	parse_kv_str(str(typ_row?.['doc_seq_typ_pdf.doc_kv_small']), doc_kv_small)
	parse_kv_str(str(row['doc.kv_doc_small']), doc_kv_small)

	// warning box from kv_boxes or fallback for a-skatt types
	const kv_boxes_str = str(typ_row?.['doc_seq_typ_pdf.kv_boxes']) || ''
	const warning = kv_boxes_str
		? parse_warning_kv(kv_boxes_str)
		: (doc.seq_typ === 'SA' || doc.seq_typ === 'SAC') ? {
			title: 'Mottagaren har skattemässiga förpliktelser',
			body: 'Mottagaren, som innehar A-skattsedel, åtar sig fullständigt ansvar för korrekt redovisning av royaltytransaktionen samt betalning av samtliga tillhörande skatter, avgifter och andra skattemässiga förpliktelser enligt tillämplig lagstiftning. Avsändaren friskriver sig härmed från samtligt ansvar och förpliktelser avseende skattefrågor relaterade till royaltytransaktionen.',
			body2: 'Avsändaren kommer att skicka en kontrolluppgift (KU-10) till både mottagaren och skattemyndigheten i Januari nästa år.',
		} : undefined

	// payment details from recipient's bank info
	const payment = build_payment_from_contact(doc.contact_row)

	const table_note = table_text || str(row['doc.table_note']) || undefined

	const payout_columns = [
		{key: 'work', title: 'Verk', width: 'flex' as const, flex_grow: 1.2, align: 'left' as const},
		{key: 'who', title: 'Teater / Förlag', width: 'flex' as const, flex_grow: 1, align: 'left' as const},
		{key: 'gross', title: 'Brutto', width: 62, align: 'right' as const, dim: true},
		{key: 'commission', title: 'Provision', width: 62, align: 'right' as const, dim: true},
		{key: 'net', title: 'Belopp', width: 62, align: 'right' as const},
		{key: 'ccy', title: 'Val.', width: 28, align: 'right' as const, dim: true},
	]

	return {
		logo_asset_ref: logo_ref || undefined,
		title: pdf_title,
		subtitle: pdf_subtitle,
		doc_ccy: ccy,
		src,
		dst,
		doc: {kv: doc_kv, kv_small: doc_kv_small},
		table: {columns: payout_columns, lines, note: table_note, totals_parts, totals: {title: 'Att utbetala', amount: {amount: total_net, ccy}}},
		payment,
		footer: footer_text ? {text: footer_text} : undefined,
		warning,
	}
}

/** compute net total for a pending doc */
export const pending_doc_net_total = (doc: PendingDoc): number => {
	return doc.lines.reduce((s, l) => {
		const gross = num(l['author_payout_line.gross_amount'])
		const pct = num(l['author_payout_line.commission_pct'])
		const commission = num(l['author_payout_line.commission_amount'] ?? (gross * pct))
		return s + (gross - commission)
	}, 0)
}

// -- helpers --

export const str = (v: unknown): string => {
	if (v === null || v === undefined) return ''
	return String(v)
}

export const num = (v: unknown): number => {
	if (v === null || v === undefined) return 0
	const n = Number(v)
	return isNaN(n) ? 0 : n
}

type SrcResult = {src: {name: string; address_lines: string[]; kv: KVPair[]; kv_small: KVPair[]}; logo_ref: string}

const build_src_from_sender = (sender_ref: string, kontakter?: ParsedSheet): SrcResult => {
	const fallback: SrcResult = {src: {name: '(avsändare saknas)', address_lines: [], kv: [], kv_small: []}, logo_ref: ''}

	if (!sender_ref || !kontakter) return fallback

	const sender_row = kontakter.rows.find(r => str(r['party.name_norm']) === sender_ref)
	if (!sender_row) return {src: {...fallback.src, name: sender_ref}, logo_ref: ''}

	const name = str(sender_row['party.name_norm'])
	const lines: string[] = []
	const addr1 = str(sender_row['contact.address.line1'])
	const addr2 = str(sender_row['contact.address.line2'])
	if (addr1) lines.push(addr1)
	if (addr2) lines.push(addr2)

	const kv: KVPair[] = []
	const org_nr = str(sender_row['contact.tax.tin'])
	if (org_nr) kv.push({key: 'Org.nr', value: org_nr})
	const vat_nr = str(sender_row['contact.tax.vat_nr'])
	if (vat_nr) kv.push({key: 'VAT', value: vat_nr})

	const kv_small: KVPair[] = []
	const email = str(sender_row['contact.email'])
	if (email) kv_small.push({key: 'E-post', value: email})
	const phone = str(sender_row['contact.phone'])
	if (phone) kv_small.push({key: 'Tel', value: phone})
	const bankgiro = str(sender_row['bank_account.se_payable'])
	if (bankgiro) kv_small.push({key: 'Bankgiro', value: bankgiro})
	const iban = str(sender_row['bank_account.iban'])
	if (iban) kv_small.push({key: 'IBAN', value: iban})
	const bic = str(sender_row['bank_account.bic'])
	if (bic) kv_small.push({key: 'BIC', value: bic})

	const logo_ref = str(sender_row['brand.logo_ref'])

	return {src: {name, address_lines: lines, kv, kv_small}, logo_ref}
}

/** parse "key:value;key:value" kv string, appending to target (skips duplicates by key) */
const parse_kv_str = (s: string, target: KVPair[]) => {
	if (!s) return
	for (const part of s.split(';')) {
		const [k, ...rest] = part.split(':')
		if (k && rest.length > 0) {
			const key = k.trim()
			const value = rest.join(':').trim()
			if (key && value && !target.some(kv => kv.key.toLowerCase() === key.toLowerCase())) {
				target.push({key, value})
			}
		}
	}
}

/** parse kv_boxes string into warning object: "title;body;body2" (semicolon-separated) */
const parse_warning_kv = (kv_boxes: string): {title: string; body: string; body2?: string} | undefined => {
	const parts = kv_boxes.split(';').map(s => s.trim()).filter(Boolean)
	if (parts.length === 0) return undefined
	return {title: parts[0]!, body: parts[1] || '', body2: parts[2] || undefined}
}

/** build payment section from recipient's bank details */
const build_payment_from_contact = (contact_row?: Record<string, unknown>): {kv: KVPair[]} | undefined => {
	if (!contact_row) return undefined
	const name = str(contact_row['party.name_norm'])
	const kv: KVPair[] = []
	if (name) kv.push({key: 'Betalning till', value: name})
	const bankgiro = str(contact_row['bank_account.se_payable'])
	if (bankgiro) kv.push({key: 'Bankgiro', value: bankgiro})
	const iban = str(contact_row['bank_account.iban'])
	if (iban) kv.push({key: 'IBAN', value: iban})
	const bic = str(contact_row['bank_account.bic'])
	if (bic) kv.push({key: 'BIC', value: bic})
	return kv.length > 0 ? {kv} : undefined
}

const build_dst_from_contact = (contact_row?: Record<string, unknown>) => {
	if (!contact_row) return {name: '(okänd)', address_lines: [], kv: [], kv_small: []}

	const name = str(contact_row['party.name_norm'])
	const lines: string[] = []
	const addr1 = str(contact_row['contact.address.line1'])
	const addr2 = str(contact_row['contact.address.line2'])
	if (addr1) lines.push(addr1)
	if (addr2) lines.push(addr2)

	const kv: KVPair[] = []
	const tin = str(contact_row['contact.tax.tin'])
	if (tin) kv.push({key: 'Personnr/Org.nr', value: tin})
	const vat_nr = str(contact_row['contact.tax.vat_nr'])
	if (vat_nr) kv.push({key: 'VAT-nr', value: vat_nr})

	const kv_small: KVPair[] = []
	const email = str(contact_row['contact.email'])
	if (email) kv_small.push({key: 'E-post', value: email})

	return {name, address_lines: lines, kv, kv_small}
}
